/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
export const LISTING_TYPE_REQUEST                      = 'hourly-request';
export const LISTING_TYPE_RESPONSE                     = 'hourly-booking';
export const LISTING_FIELD_REQUEST_LISTING_ID          = 'requestID';
export const LISTING_FIELD_RESPONSE_LISTING_ID         = 'responseID';
export const LISTING_FIELD_DUE_IN_DAYS                 = 'dueInDays';
export const LISTING_FIELD_DUE_DATE                    = 'dueDate';
export const LISTING_FIELDS_TO_COPY_FROM_REQUEST       = ['expertise'];
export const HAS_LINKS_BETWEEN_REQUESTS_AND_RESPONSES  = false;
export const SHOULD_SHOW_PAST_DUE_LISTINGS             = false;
export const SHOULD_ALWAYS_CREATE_NEW_LISTING          = false;
export const SHOULD_CREATE_BASED_ON_REQUEST_TYPE       = false;
export const IS_REQUEST_LISTING_PRICE_REQUIRED         = false;
export const ARE_REQUEST_LISTING_PHOTOS_REQUIRED       = false;

export const LISTING_TYPE_IDS_DAYS_FEE = {[LISTING_TYPE_REQUEST]:{"30":99, "90":199}}
/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  {
    key: 'certifications',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'AI experience', label: 'AI experience' },
      { option: 'B Corp', label: 'B Corp' },
      { option: 'Business Energy Professional (BEP)', label: 'Business Energy Professional (BEP)' },
      { option: 'Carbon Auditing Professional (CAP)', label: 'Carbon Auditing Professional (CAP)' },
      { option: 'Certified Building Commissioning Professional (CBCP)', label: 'Certified Building Commissioning Professional (CBCP)' },
      { option: 'Certified Climate Change Professional (CC-P)', label: 'Certified Climate Change Professional (CC-P)' },
      { option: 'Certified Demand Side Manager (CDSM)', label: 'Certified Demand Side Manager (CDSM)' },
      { option: 'Certified Energy Auditor/Analyst (CEA)', label: 'Certified Energy Auditor/Analyst (CEA)' },
      { option: 'Certified Energy Manager (CEM)', label: 'Certified Energy Manager (CEM)' },
      { option: 'Certified Energy Procurement Professional (CEP)', label: 'Certified Energy Procurement Professional (CEP)' },
      { option: 'Certified GeoExchange Designer (CGD) (Heat Pump)', label: 'Certified GeoExchange Designer (CGD) (Heat Pump)' },
      { option: 'Certified Green Building Engineer (GBE)', label: 'Certified Green Building Engineer (GBE)' },
      { option: 'Certified Industrial Energy Professional (CIEP)', label: 'Certified Industrial Energy Professional (CIEP)' },
      { option: 'Certified Lighting Efficiency Professional (CLEP)', label: 'Certified Lighting Efficiency Professional (CLEP)' },
      { option: 'Certified Measurement & Verification Professional (CMVP)', label: 'Certified Measurement & Verification Professional (CMVP)' },
      { option: 'Certified Practitioner in Energy Management Systems (50001 CP EnMS)', label: 'Certified Practitioner in Energy Management Systems (50001 CP EnMS)' },
      { option: 'Certified Renewable Energy Professional (REP)', label: 'Certified Renewable Energy Professional (REP)' },
      { option: 'Certified Sustainable Development Professional (CSDP)', label: 'Certified Sustainable Development Professional (CSDP)' },
      { option: 'Certified Water Efficiency Professional (CWEP)', label: 'Certified Water Efficiency Professional (CWEP)' },
      { option: 'Climate Resilience and Adaptation', label: 'Climate Resilience and Adaptation' },
      { option: 'Distributed Generation Certified Professional (DGCP)', label: 'Distributed Generation Certified Professional (DGCP)' },
      { option: 'ESG', label: 'ESG' },
      { option: 'Energy Efficiency Practitioner (EEP)', label: 'Energy Efficiency Practitioner (EEP)' },
      { option: 'Energy Modeling', label: 'Energy Modeling' },
      { option: 'Energy Ratings', label: 'Energy Ratings' },
      { option: 'Energy Star', label: 'Energy Star' },
      { option: 'GRI Reporting', label: 'GRI Reporting' },
      { option: 'Geographic Information Systems Professional (GIS/GISP)', label: 'Geographic Information Systems Professional (GIS/GISP)' },
      { option: 'Graduate Level Environmental Studies', label: 'Graduate Level Environmental Studies' },
      { option: 'Green Building', label: 'Green Building' },
      { option: 'Green Globes Professional (GGP)', label: 'Green Globes Professional (GGP)' },
      { option: 'Green IT/Green Coding', label: 'Green IT/Green Coding' },
      { option: 'GreenBiz', label: 'GreenBiz' },
      { option: 'GreenPoint Ratings', label: 'GreenPoint Ratings' },
      { option: 'HERS Ratings', label: 'HERS Ratings' },
      { option: 'ISO 50001 Lead Auditor (50001 LA)', label: 'ISO 50001 Lead Auditor (50001 LA)' },
      { option: 'Leadership in Energy and Environmental Design (LEED)', label: 'Leadership in Energy and Environmental Design (LEED)' },
      { option: 'Life Cycle Assessment Certified Professional (LCACP)', label: 'Life Cycle Assessment Certified Professional (LCACP)' },
      { option: 'Project Management (PM/PMP)', label: 'Project Management (PM/PMP)' },
      { option: 'Remote Only', label: 'Remote Only' },
      { option: 'Renewables', label: 'Renewables (solar, wind, hydro, biofuels…)' },
      { option: 'Speaking Engagement(s) or Training Session(s)', label: 'Speaking Engagement(s) or Training Session(s)' },
      { option: 'Superior Energy Performance Verifier (SEP-PV)', label: 'Superior Energy Performance Verifier (SEP-PV)' },
      { option: 'Sustainability Accounting Standards Board Reporting (SASB)', label: 'Sustainability Accounting Standards Board Reporting (SASB)' },
      { option: 'Sustainability Excellence Analyst/Professional (SEA/SEP)', label: 'Sustainability Excellence Analyst/Professional (SEA/SEP)' },
      { option: 'Sustainability Software Analysis', label: 'Sustainability Software Analysis' },
      { option: 'Sustainable/Regenerative Agriculture', label: 'Sustainable/Regenerative Agriculture' },
      { option: 'TCFD Practices', label: 'TCFD Practices' },
      { option: 'TRUE Ratings', label: 'TRUE Ratings' },
      { option: 'Title 24 / CALGreen', label: 'Title 24 / CALGreen' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Certifications & Expertise',
      searchMode: 'has_any',
      group: 'secondary',
    },
    showConfig: {
      label: 'Certifications & Expertise',
      isDetail: true,
    },
    saveConfig: {
      label: 'Certifications & Expertise',
      placeholderMessage: 'Select an option…',
      isRequired: false,
    },
  },
  {
    key: LISTING_FIELD_DUE_IN_DAYS,
    scope: 'public',
    includeForListingTypes: [],
    schemaType: 'enum',
    enumOptions: Object.entries(LISTING_TYPE_IDS_DAYS_FEE[LISTING_TYPE_REQUEST]).map(([key, value]) => ({ option: key, label: `${key} days - $${value}` })),
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: [LISTING_TYPE_REQUEST],
    },
    saveConfig: {
      label: 'Duration',
      placeholderMessage: 'Select a date…',
      isRequired: true,
      requiredMessage: 'Duration is required.',
    },
  },
  {
    key: LISTING_FIELD_DUE_DATE,
    scope: 'public',
    includeForListingTypes: [],
    schemaType: 'date',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: [LISTING_TYPE_REQUEST],
    },
    showConfig: {
      label: 'Closing date',
      isDetail: true,
    },
    saveConfig: {
      label: 'Closing date',
      isVisible: false,
    },
  },
  // {
  //   "scope": "public",
  //   "label": "Gears",
  //   "key": "gears",
  //   "schemaType": "long",
  //   "numberConfig": {
  //     "minimum": 1,
  //     "maximum": 24
  //   },
  //   "filterConfig": {
  //     "indexForSearch": true,
  //     "group": "primary",
  //     "label": "Gears"
  //   }
  // }
  // {
  //   key: 'bikeType',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'city-bikes', label: 'City bikes' },
  //     { option: 'electric-bikes', label: 'Electric bikes' },
  //     { option: 'mountain-bikes', label: 'Mountain bikes' },
  //     { option: 'childrens-bikes', label: "Children's bikes" },
  //   ],
  //   categoryConfig: {
  //     limitToCategoryIds: true,
  //     categoryIds: ['cats'],
  //   },
  //   filterConfig: {
  //     indexForSearch: true,
  //     filterType: 'SelectMultipleFilter', //'SelectSingleFilter',
  //     label: 'Bike type',
  //     group: 'primary',
  //   },
  //   showConfig: {
  //     label: 'Bike type',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Bike type',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a bike type.',
  //   },
  // },
  // {
  //   key: 'tire',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: '29', label: '29' },
  //     { option: '28', label: '28' },
  //     { option: '27', label: '27' },
  //     { option: '26', label: '26' },
  //     { option: '24', label: '24' },
  //     { option: '20', label: '20' },
  //     { option: '18', label: '18' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Tire size',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Tire size',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Tire size',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a tire size.',
  //   },
  // },
  // {
  //   key: 'brand',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'cube', label: 'Cube' },
  //     { option: 'diamant', label: 'Diamant' },
  //     { option: 'ghost', label: 'GHOST' },
  //     { option: 'giant', label: 'Giant' },
  //     { option: 'kalkhoff', label: 'Kalkhoff' },
  //     { option: 'kona', label: 'Kona' },
  //     { option: 'otler', label: 'Otler' },
  //     { option: 'vermont', label: 'Vermont' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Brand',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Brand',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Brand',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a brand.',
  //   },
  // },
  // {
  //   key: 'accessories',
  //   scope: 'public',
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'bell', label: 'Bell' },
  //     { option: 'lights', label: 'Lights' },
  //     { option: 'lock', label: 'Lock' },
  //     { option: 'mudguard', label: 'Mudguard' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Accessories',
  //     searchMode: 'has_all',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Accessories',
  //   },
  //   saveConfig: {
  //     label: 'Accessories',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: false,
  //   },
  // },
  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['product-selling'],
  //   },
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['daily-booking'],
  //   },
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

export const defaultTransactionProcessAlias = process.env.REACT_APP_TRANSACTION_PROCESS_ALIAS ?? 'release-1';

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

export const listingTypes = [
  // {
  //   listingType: 'daily-booking',
  //   label: 'Daily booking',
  //   transactionType: {
  //     process:  'default-booking',
  //     alias:    `default-booking/${defaultTransactionProcessAlias}`,
  //     unitType: 'day',
  //   },
  //   defaultListingFields: {
  //     location: true,
  //     payoutDetails: true,
  //   },
  // },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;

export const listingTypeFieldForFilter = {
  key: 'listingType',
  scope: 'public',
  schemaType: 'enum',
  enumOptions: [
    { option: LISTING_TYPE_REQUEST, label: 'Requests' },
    { option: LISTING_TYPE_RESPONSE, label: 'Responses' },
  ],
  filterConfig: {
    indexForSearch: true,
    label: 'Listing Type',
    group: 'primary',
    filterType: 'SelectSingleFilter',
  },
};
